import * as Sentry from '@sentry/browser';

const SENSITIVE_FIELDS = ['password', 'token', 'secret', 'api_key', 'authorization'];

/**
 * Mask sensitive values (recursively) before anything is sent to GlitchTip
 * @param {*} logEntry - Object, array, JSON string or primitive to sanitize
 */
export const sanitize = (logEntry) => {
    if (!logEntry) return logEntry;

    // Handle strings (could be JSON strings)
    if (typeof logEntry === 'string') {
        try {
            const parsed = JSON.parse(logEntry);
            return JSON.stringify(sanitize(parsed));
        } catch (e) {
            // Not JSON, return as is
            return logEntry;
        }
    }

    // Handle arrays
    if (Array.isArray(logEntry)) {
        return logEntry.map(item => sanitize(item));
    }

    // Handle objects
    if (typeof logEntry === 'object') {
        const sanitized = {};
        for (const [key, value] of Object.entries(logEntry)) {
            const lowerKey = key.toLowerCase();

            // Check if this field should be masked
            const isSensitive = SENSITIVE_FIELDS.some(field =>
                lowerKey.includes(field.toLowerCase())
            );

            if (isSensitive && value) {
                // Mask the sensitive value
                sanitized[key] = '****';
            } else if (typeof value === 'object') {
                // Recursively sanitize nested objects/arrays
                sanitized[key] = sanitize(value);
            } else {
                sanitized[key] = value;
            }
        }
        return sanitized;
    }

    return logEntry;
};

const glitchTipService = {
    /**
     * Report a React render crash caught by the ErrorBoundary
     * @param {Error} error - Error object
     * @param {Object} errorInfo - React error info (contains componentStack)
     */
    logPageCrash(error, errorInfo = null) {
        Sentry.captureException(error, {
            tags: { type: 'page_crash' },
            contexts: { react: { componentStack: errorInfo?.componentStack } }
        });
    },

    /**
     * Report a failed API response
     * @param {Object} error - Axios error
     * @param {Object} requestInfo - Extra request information
     */
    logApiError(error, requestInfo = {}) {
        const url = error.config?.url;
        const method = error.config?.method;
        const status = error.response?.status;

        Sentry.captureException(error, {
            // request_id matches the X-Request-ID header the backend received
            tags: { type: 'api_error', method, status, request_id: requestInfo.requestId },
            extra: sanitize({
                url,
                method,
                status,
                statusText: error.response?.statusText,
                ...requestInfo,
                // Axios serializes request bodies to JSON strings, which sanitize()
                // only parses at the top level, so mask the bodies explicitly.
                requestData: sanitize(error.config?.data),
                responseData: sanitize(error.response?.data)
            }),
            // Every AxiosError shares the same stack, so group per endpoint instead
            fingerprint: ['api_error', String(method), String(status), String(url).split('?')[0]]
        });
    },

    /**
     * Report a request that failed before it was sent
     * @param {Object} error - Axios request error
     */
    logRequestError(error) {
        Sentry.captureException(error, {
            tags: { type: 'request_error' },
            extra: sanitize({
                url: error.config?.url,
                method: error.config?.method,
                headers: error.config?.headers
            })
        });
    },

    /**
     * Send a test error and wait for GlitchTip's answer
     * @param {number} timeout - Milliseconds to wait for the server's answer
     * @returns {Promise<{status: 'sent'|'failed'|'disabled', statusCode?: number, eventId?: string}>}
     */
    sendTestEvent(timeout = 10000) {
        const client = Sentry.getClient();
        if (!client?.getDsn()) {
            return Promise.resolve({ status: 'disabled' });
        }

        return new Promise((resolve) => {
            let eventId;
            const finish = (status, statusCode) => {
                unsubscribe();
                clearTimeout(timer);
                resolve({ status, statusCode, eventId });
            };

            // The SDK reports { statusCode } once GlitchTip answered, or {} when it could not deliver
            const unsubscribe = client.on('afterSendEvent', (event, sendResponse) => {
                if (event.event_id !== eventId) return;
                const statusCode = sendResponse?.statusCode;
                finish(statusCode >= 200 && statusCode < 300 ? 'sent' : 'failed', statusCode);
            });
            const timer = setTimeout(() => finish('failed'), timeout);

            // The message is unique because the SDK drops an event identical to the previous one
            eventId = Sentry.captureException(new Error(`GlitchTip connectivity test ${new Date().toISOString()}`), {
                tags: { type: 'connectivity_test' },
                fingerprint: ['connectivity_test']
            });
        });
    }
};

export default glitchTipService;
